import { v4 as uuidv4 } from 'uuid';
import { CarouselItem, MediaURL } from '../components/template/ImageCarousel';
import {
  ImageCarouselType,
  LeftRightListType,
  StandardHeaderTextType,
  SubtitleType,
  TableType,
  TextType,
  HeroImageType,
} from '../models/ContentManagementInterface';
import { ContentHeadingProps, FootNoteProps } from '../models/TopLevelTemplateComponentProps';

const SAMPLE_CAROUSEL_ALT_TEXT = 'Sample Image 3';
const DEFAULT_TEXT_CONTENT = 'insert text here';

export function generateStandardHeaderTextType(): StandardHeaderTextType {
  return {
    type: 'StandardHeaderText',
    id: newContentId(),
    properties: {
      id: newContentId(),
      heading: newHeadingContent(),
      content: newTextContent(),
      openSimplified: true,
    },
  };
}

export function generateSubtitleType(): SubtitleType {
  return {
    type: 'Subtitle',
    id: newContentId(),
    properties: { heading: { id: newContentId(), content: '' } },
  };
}

export function generateTextType(): TextType {
  return {
    type: 'Text',
    id: newContentId(),
    properties: { content: { id: newContentId(), content: '' } },
  };
}

export function generateHeroImageType(): HeroImageType {
  return {
    type: 'HeroImage',
    id: newContentId(),
    properties: { imageUrl: { id: newContentId(), url: '' } },
  };
}

export function generateImageCarouselType(_modelId: string, _modelType: string): ImageCarouselType {
  return {
    type: 'ImageCarousel',
    id: newContentId(),
    properties: {
      id: newContentId(),
      heading: newHeadingContent(),
      items: [
        {
          imageUrl: {
            id: newContentId(),
            url: '/assets/carousel_sample_1.jpg',
          },
          altText: newTextContent(SAMPLE_CAROUSEL_ALT_TEXT),
          text: newTextContent(),
          heading: newTextContent(),
        },
        {
          imageUrl: {
            id: newContentId(),
            url: '/assets/carousel_sample_2.jpg',
          },
          altText: newTextContent(SAMPLE_CAROUSEL_ALT_TEXT),
          text: newTextContent(),
          heading: newTextContent(),
        },
        {
          imageUrl: {
            id: newContentId(),
            url: '/assets/carousel_sample_3.jpg',
          },
          altText: newTextContent(SAMPLE_CAROUSEL_ALT_TEXT),
          text: newTextContent(),
          heading: newTextContent(),
        },
      ],
    },
  };
}

export function generateLeftRightListType(): LeftRightListType {
  return {
    type: 'LeftRightList',
    id: newContentId(),
    properties: {
      id: newContentId(),
      heading: newHeadingContent(),
      items: [],
      openSimplified: true,
    },
  };
}

export function generateTableType(): TableType {
  return {
    type: 'Table',
    id: newContentId(),
    properties: {
      id: newContentId(),
      heading: newHeadingContent(),
      rows: [
        {
          cells: [
            { id: newContentId(), content: { id: newContentId(), content: '' } },
            { id: newContentId(), content: { id: newContentId(), content: '' } },
          ],
        },
      ],
    },
  };
}

export function newContentId() {
  return uuidv4();
}

export function newTextContent(text?: string) {
  return { id: newContentId(), content: text || DEFAULT_TEXT_CONTENT };
}

export function tagPlaceholder(suffix?: string) {
  return `tag placeholder ${suffix || ''}`.trim();
}

export function headingPlaceholder(suffix?: string) {
  return `heading placeholder ${suffix || ''}`.trim();
}

export function subHeadingPlaceholder(suffix?: string) {
  return `subheading placeholder ${suffix || ''}`.trim();
}

export function contentPlaceholder(suffix?: string) {
  return `${DEFAULT_TEXT_CONTENT} ${suffix || ''}`.trim();
}

export function newHeadingContent(text?: string, tag?: string, subHeader?: string | null) {
  if (!text) {
    return {
      ...newTextContent(headingPlaceholder()),
      tag: tagPlaceholder(),
      subHeader: null,
    };
  }

  if (tag) {
    return {
      id: newContentId(),
      content: text,
      tag: tag,
      subHeader: subHeader,
    };
  }

  if (subHeader) {
    return {
      id: newContentId(),
      content: text,
      tag: text,
      subHeader: subHeader,
    };
  }

  return { id: newContentId(), content: text, tag: text, subHeader: null };
}

export function newContentAndHeading(heading?: string, text?: string) {
  return { heading: newTextContent(heading), text: newTextContent(text) };
}

export function newMediaUrl(url?: string, isVideo?: boolean) {
  return {
    id: newContentId(),
    url: url || (isVideo ? undefined : '/assets/carousel_sample_1.jpg'),
  };
}

export function newImageCarouselItem(): CarouselItem {
  return {
    heading: newTextContent(),
    imageUrl: newMediaUrl() as MediaURL,
    altText: newTextContent(),
  };
}

export function headingNotSet(heading: ContentHeadingProps | null | undefined) {
  return (
    (heading?.content || '').trim() == '' ||
    heading?.content?.includes(headingPlaceholder()) ||
    heading?.content?.includes(DEFAULT_TEXT_CONTENT)
  );
}

export function headingSet(heading: ContentHeadingProps | null | undefined) {
  return !headingNotSet(heading);
}

export function subHeadingNotSet(heading: ContentHeadingProps | null | undefined) {
  return (
    (heading?.subHeader || '').trim() == '' ||
    heading?.subHeader?.includes(subHeadingPlaceholder()) ||
    heading?.tag?.includes(DEFAULT_TEXT_CONTENT)
  );
}

export function subHeadingSet(heading: ContentHeadingProps | null | undefined) {
  return !subHeadingNotSet(heading);
}

export function tagNotSet(heading: ContentHeadingProps | null | undefined) {
  return (
    (heading?.tag || '').trim() == '' ||
    heading?.tag?.includes(tagPlaceholder()) ||
    heading?.tag?.includes(DEFAULT_TEXT_CONTENT)
  );
}

function contentTextValue(content: string | null | undefined): string {
  const div = document.createElement('div');
  div.innerHTML = content || '';

  return div.textContent || div.innerText || '';
}

function contentIsEffectivelyEmpty(content: string): boolean {
  return contentTextValue(content).trim() === '';
}

export function contentNotSet(content: { id: string; content: string } | null) {
  return (
    contentIsEffectivelyEmpty(content?.content || '') ||
    contentTextValue(content?.content).includes(contentPlaceholder())
  );
}

export function contentSet(content: { id: string; content: string } | null) {
  return !contentNotSet(content);
}

export function tagSet(heading: ContentHeadingProps | null | undefined) {
  return !tagNotSet(heading);
}

/**
 * A section's short label (e.g. the investor page's section filter chips): its
 * tag, else its heading — or '' when neither is really set, so an untouched
 * "heading placeholder" ghost heading never shows up as a label.
 */
export function tagOrHeading(heading: ContentHeadingProps) {
  if (tagSet(heading)) {
    return heading.tag;
  }

  return headingNotSet(heading) ? '' : heading.content;
}

export function newFootNote(number: number, content: string = '') {
  return {
    id: newContentId(),
    number: number,
    content: content,
  };
}

export function processFootnoteReferences(content: string, footnotes?: FootNoteProps[]): string {
  if (!footnotes || footnotes.length === 0) {
    return content;
  }

  // Replace [1], [2], etc. with clickable footnote references
  return content.replace(/\[footnote:(\d+)\]/g, (match, number) => {
    const footnoteNumber = parseInt(number, 10);
    const footnote = footnotes.find((fn) => fn.number === footnoteNumber);
    if (footnote) {
      return `<sup style="color: inherit; cursor: pointer;">${footnoteNumber}</sup>`;
    }
    return match;
  });
}
